import { useState } from 'react';
import { InstantSearch, Configure, useHits, Stats, HitsPerPage, SortBy, Pagination, ClearRefinements, CurrentRefinements, RefinementList, HierarchicalMenu, ToggleRefinement } from 'react-instantsearch';
import { searchClient } from '../../services/algoliaClient';
import { insights } from '../../services/algoliaInsights';
import { useStore } from '../../context/StoreContext';
import { ProductHit } from '../Search/ProductHit';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Funnel, X } from '@phosphor-icons/react';
import { getCategoryBySlug, getFilterAttribute } from '../../data/categories';

// Custom Range Slider Component (same as SearchPage)
const RangeSlider = ({ attribute, min = 0, max = 1000, onRangeChange }) => {
  const [range, setRange] = useState({ min, max });
  const [isDragging, setIsDragging] = useState(false);

  const handleMinChange = (e) => {
    const newMin = Math.min(parseInt(e.target.value), range.max);
    setRange(prev => ({ ...prev, min: newMin }));
    onRangeChange && onRangeChange({ min: newMin, max: range.max });
  };

  const handleMaxChange = (e) => {
    const newMax = Math.max(parseInt(e.target.value), range.min);
    setRange(prev => ({ ...prev, max: newMax }));
    onRangeChange && onRangeChange({ min: range.min, max: newMax });
  };

  const getSliderBackground = () => {
    const minPercent = ((range.min - min) / (max - min)) * 100;
    const maxPercent = ((range.max - min) / (max - min)) * 100;
    return `linear-gradient(to right, #e9ecef 0%, #e9ecef ${minPercent}%, #4B164C ${minPercent}%, #4B164C ${maxPercent}%, #e9ecef ${maxPercent}%, #e9ecef 100%)`;
  };

  return (
    <div className="custom-range-slider">
      <div className="range-slider-container">
        <div 
          className="range-slider-track"
          style={{ background: getSliderBackground() }}
        >
          <input
            type="range"
            min={min}
            max={max}
            value={range.min}
            onChange={handleMinChange}
            className="range-slider-input range-slider-input-min"
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={() => setIsDragging(false)}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => setIsDragging(false)}
          />
          <input
            type="range"
            min={min}
            max={max}
            value={range.max}
            onChange={handleMaxChange}
            className="range-slider-input range-slider-input-max"
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={() => setIsDragging(false)}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => setIsDragging(false)}
          />
        </div>
      </div>
      
      <div className="range-values">
        <div className="range-value">
          <label>Min: ${range.min}</label>
        </div>
        <div className="range-value">
          <label>Max: ${range.max}</label>
        </div>
      </div>
      
      <button 
        className="range-apply-btn"
        onClick={() => onRangeChange && onRangeChange(range)}
      >
        Appliquer
      </button>
    </div>
  );
};

// Category Page Component
export const CategoryPage = () => {
  const { t } = useTranslation();
  const { category, type, subcategory } = useParams();
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [storeFilter, setStoreFilter] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { selectedStore } = useStore();
  
  // Get category data from slug
  const categoryData = getCategoryBySlug(category);
  
  // Determine if this is a wine type category or a grape/region category
  const isWineTypeCategory = categoryData !== undefined;
  const isStructuredCategory = type && subcategory;
  
  // Get category title
  const getCategoryTitle = () => {
    if (isWineTypeCategory) {
      return categoryData.name;
    } else if (isStructuredCategory) {
      // This is a structured category (e.g., /categories/regions/loire)
      return subcategory;
    } else if (category) {
      // This is a grape/region category (e.g., /merlot)
      return category;
    }
    return 'Catégorie';
  };

  const CustomHits = () => {
    const { items, sendEvent } = useHits();
    
    if (items.length === 0) {
      return (
        <div className="no-search-results">
          <h3>Aucun résultat</h3>
          <p>Nous n'avons trouvé aucun produit dans cette catégorie.</p>
        </div>
      );
    }

    return (
      <div className={`results-container ${viewMode}`}>
        {items.map((hit) => (
          <ProductHit key={hit.objectID} hit={hit} showFullDetails={true} sendEvent={sendEvent} />
        ))}
      </div>
    );
  };
  
  // Build the filter string
  const getFilters = () => {
    let filters = '';
    
    if (isWineTypeCategory) {
      // This is a wine type category (e.g., /categories/vin-rouge)
      filters = `attributes.identite_produit:'${categoryData.id}'`;
      
      // Check for additional filters from URL parameters
      const filterParam = searchParams.get('filter');
      if (filterParam) {
        const [attribute, value] = filterParam.split(':');
        if (attribute && value) {
          const decodedValue = decodeURIComponent(value);
          if (filters) filters += ' AND ';
          filters += `${attribute}:'${decodedValue}'`;
        }
      }
    } else if (isStructuredCategory) {
      // This is a structured category (e.g., /categories/regions/loire)
      const categoryName = subcategory;
      
      // Apply filter based on the type
      if (type === 'regions') {
        filters = `attributes.region_origine:'${categoryName}'`;
      } else if (type === 'cepages') {
        filters = `attributes.cepage:'${categoryName}'`;
      } else if (type === 'pays') {
        filters = `attributes.pays_origine:'${categoryName}'`;
      }
    } else if (category) {
      // This is a grape/region category (e.g., /merlot) - fallback for old URLs
      const categoryName = category;
      
      // Check if it's a grape variety (common grape names)
      const grapeVarieties = [
        'merlot', 'cabernet', 'sauvignon', 'chardonnay', 'pinot', 'syrah', 
        'grenache', 'cinsault', 'mourvèdre', 'sangiovese', 'nebbiolo', 'tempranillo'
      ];
      
      // Check if it's a region (common wine regions)
      const regions = [
        'bordeaux', 'bourgogne', 'champagne', 'loire', 'rhône', 'rhone', 'alsace',
        'provence', 'languedoc', 'beaujolais', 'côtes', 'cotes'
      ];
      
      if (grapeVarieties.some(grape => categoryName.toLowerCase().includes(grape))) {
        filters = `attributes.cepage:'${categoryName}'`;
      } else if (regions.some(region => categoryName.toLowerCase().includes(region))) {
        filters = `attributes.region_origine:'${categoryName}'`;
      } else {
        // Default to grape variety if uncertain
        filters = `attributes.cepage:'${categoryName}'`;
      }
    }
    
    // Add store filter if enabled
    if (storeFilter) {
      if (filters) filters += ' AND ';
      filters += `attributes.store_availability_list:${selectedStore}`;
    }
    
    // Add price range filter
    if (priceRange.min > 0 || priceRange.max < 1000) {
      if (filters) filters += ' AND ';
      filters += `price:${priceRange.min} TO ${priceRange.max}`;
    }
    
    return filters;
  };

  const handlePriceRangeChange = (newRange) => {
    setPriceRange(newRange);
  };

  return (
    <main className="category-page">
      <InstantSearch searchClient={searchClient} indexName="saq_products" insights={{ insightsInitParams: { userToken: insights.userToken } }}>
        <Configure
          query=""
          filters={getFilters()}
          optionalFilters={[`attributes.store_availability_list:${selectedStore}<score=10>`]}
          attributesToRetrieve={['objectID', 'name', 'sku', 'image', 'price', 'attributes', 'color', 'inStock', 'store_data', 'categorias_flat']}
        />
        
        <div className="category-interface">
          <div className="category-header">
            <div className="category-title">
              <h1>{getCategoryTitle()}</h1>
              <div className="category-breadcrumb">
                <Link to="/">Accueil</Link> &gt; 
                <Link to="/categories">Catégories</Link> &gt; 
                {getCategoryTitle()}
              </div>
            </div>
            
            <div className="category-stats">
              <Stats />
            </div>
          </div>

          {/* Mobile Controls */}
          <div className="mobile-controls">
            <div className="mobile-controls-row">
              <span className="mobile-control-label">Filtres</span>
              <button 
                className="mobile-filter-button"
                onClick={() => setMobileFiltersOpen(true)}
              >
                <Funnel size={16} />
                Filtres
              </button>
            </div>
            
            <div className="mobile-controls-row">
              <span className="mobile-control-label">Vue</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  className={`mobile-control-button ${viewMode === 'grid' ? 'active' : ''}`}
                  onClick={() => setViewMode('grid')}
                >
                  Grille
                </button>
                <button 
                  className={`mobile-control-button ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setViewMode('list')}
                >
                  Liste
                </button>
              </div>
            </div>
            
            <div className="mobile-controls-row">
              <span className="mobile-control-label">Trier</span>
              <SortBy
                items={[
                  { label: 'Pertinence', value: 'saq_products' },
                  { label: 'Prix: croissant', value: 'saq_products_price_asc' },
                  { label: 'Prix: décroissant', value: 'saq_products_price_desc' },
                  { label: 'Nom A-Z', value: 'saq_products_name_asc' },
                  { label: 'Nom Z-A', value: 'saq_products_name_desc' }
                ]}
                classNames={{
                  select: 'mobile-control-button'
                }}
              />
            </div>
          </div>

          {/* Desktop Controls */}
          <div className="search-controls">
            <div className="search-options">
              <div className="view-toggle">
                <label>Vue:</label>
                <div className="toggle-buttons">
                  <button 
                    className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="1" width="6" height="6" rx="1"/>
                      <rect x="9" y="1" width="6" height="6" rx="1"/>
                      <rect x="1" y="9" width="6" height="6" rx="1"/>
                      <rect x="9" y="9" width="6" height="6" rx="1"/>
                    </svg>
                    Grille
                  </button>
                  <button 
                    className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="2" y="3" width="12" height="2" rx="1"/>
                      <rect x="2" y="7" width="12" height="2" rx="1"/>
                      <rect x="2" y="11" width="12" height="2" rx="1"/>
                    </svg>
                    Liste
                  </button>
                </div>
              </div>
              
              <div className="hits-per-page">
                <label>Résultats par page:</label>
                <HitsPerPage
                  items={[
                    { label: '12 résultats', value: 12, default: true },
                    { label: '24 résultats', value: 24 },
                    { label: '48 résultats', value: 48 }
                  ]}
                />
              </div>
              
              <div className="sort-by">
                <label>Trier par:</label>
                <SortBy
                  items={[
                    { label: 'Pertinence', value: 'saq_products' },
                    { label: 'Prix: croissant', value: 'saq_products_price_asc' },
                    { label: 'Prix: décroissant', value: 'saq_products_price_desc' },
                    { label: 'Nom A-Z', value: 'saq_products_name_asc' },
                    { label: 'Nom Z-A', value: 'saq_products_name_desc' }
                  ]}
                />
              </div>
            </div>
            
            <div className="clear-filters">
              <ClearRefinements
                translations={{
                  resetButtonText: 'Effacer les filtres'
                }}
              />
            </div>
          </div>

          <div className="search-content">
            <aside className="search-sidebar">
              <div className="facets-section">
                <h3>Filtres</h3>
                
                {/* Store Availability Toggle */}
                <div className="store-filter-section" style={{ marginBottom: 24 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#fff',
                    padding: '12px 16px',
                    borderRadius: 12,
                    border: '1px solid #e8e0e8',
                    boxShadow: '0 2px 8px rgba(75,22,76,0.06)',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#4B164C' }}>
                      Disponible en ma succursale
                    </div>
                    
                    <button
                      onClick={() => setStoreFilter(!storeFilter)}
                      style={{
                        position: 'relative',
                        width: 52,
                        height: 28,
                        background: storeFilter ? '#4B164C' : '#e8e0e8',
                        border: 'none',
                        borderRadius: 14,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        padding: 2
                      }}
                      disabled={!selectedStore}
                    >
                      <div style={{
                        width: 24,
                        height: 24,
                        background: '#fff',
                        borderRadius: '50%',
                        transform: storeFilter ? 'translateX(24px)' : 'translateX(0)',
                        transition: 'transform 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <MapPin size={12} color={storeFilter ? "#4B164C" : "#888"} weight="bold" />
                      </div>
                    </button>
                  </div>
                  
                  {!selectedStore && (
                    <div style={{
                      background: '#fff3e0',
                      border: '1px solid #ffcc80',
                      borderRadius: 8,
                      padding: '12px 16px',
                      marginTop: 8,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f57c00" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      <span style={{ fontSize: 14, color: '#f57c00' }}>
                        Sélectionnez une succursale pour filtrer par disponibilité
                      </span>
                    </div>
                  )}
                </div>

                {/* Boolean Toggle Filters */}
                <div className="facet-group">
                  <h4>Statut du produit</h4>
                  <div className="toggle-filters">
                    <ToggleRefinement
                      attribute="inStock"
                      label="En stock"
                    />
                    <ToggleRefinement
                      attribute="availability_front"
                      label="Disponible en magasin"
                    />
                  </div>
                </div>

                {/* Current Refinements */}
                <div className="current-refinements">
                  <CurrentRefinements
                    translations={{
                      clearButtonText: '✕'
                    }}
                  />
                </div>

                {/* Grape Variety */}
                <div className="facet-group">
                  <h4>Cépage</h4>
                  <RefinementList
                    attribute="attributes.cepage"
                    limit={10}
                    showMore={true}
                    showMoreLimit={30}
                    translations={{
                      showMore: 'Voir plus',
                      showLess: 'Voir moins',
                      noRefinementRoot: 'Aucun cépage disponible'
                    }}
                  />
                </div>

                {/* Country of Origin */}
                <div className="facet-group">
                  <h4>Pays d'origine</h4>
                  <RefinementList
                    attribute="attributes.pays_origine"
                    limit={15}
                    showMore={true}
                    showMoreLimit={30}
                    translations={{
                      showMore: 'Voir plus',
                      showLess: 'Voir moins',
                      noRefinementRoot: 'Aucun pays disponible'
                    }}
                  />
                </div>

                {/* Region of Origin */}
                <div className="facet-group">
                  <h4>Région d'origine</h4>
                  <RefinementList
                    attribute="attributes.region_origine"
                    limit={15}
                    showMore={true}
                    showMoreLimit={30}
                    translations={{
                      showMore: 'Voir plus',
                      showLess: 'Voir moins',
                      noRefinementRoot: 'Aucune région disponible'
                    }}
                  />
                </div>

                {/* Sub-region */}
                <div className="facet-group">
                  <h4>Sous-région</h4>
                  <RefinementList
                    attribute="attributes.sous_region_origine_1"
                    limit={15}
                    showMore={true}
                    showMoreLimit={30}
                    translations={{
                      showMore: 'Voir plus',
                      showLess: 'Voir moins',
                      noRefinementRoot: 'Aucune sous-région disponible'
                    }}
                  />
                </div>

                {/* Product Status */}
                <div className="facet-group">
                  <h4>Statut du produit</h4>
                  <RefinementList
                    attribute="attributes.statut_produit"
                    limit={10}
                    showMore={true}
                    showMoreLimit={20}
                    translations={{
                      showMore: 'Voir plus',
                      showLess: 'Voir moins',
                      noRefinementRoot: 'Aucun statut disponible'
                    }}
                  />
                </div>

                {/* Product Identity */}
                <div className="facet-group">
                  <h4>Identité du produit</h4>
                  <RefinementList
                    attribute="attributes.identite_produit"
                    limit={10}
                    showMore={true}
                    showMoreLimit={20}
                    translations={{
                      showMore: 'Voir plus',
                      showLess: 'Voir moins',
                      noRefinementRoot: 'Aucune identité disponible'
                    }}
                  />
                </div>

                {/* Price Range */}
                <div className="facet-group">
                  <h4>Fourchette de prix</h4>
                  <RangeSlider
                    min={0}
                    max={1000}
                    onRangeChange={handlePriceRangeChange}
                  />
                </div>

                {/* Categories */}
                <div className="facet-group">
                  <h4>Catégories</h4>
                  <HierarchicalMenu
                    attributes={[
                      'categorias_hierarquicas.lvl0',
                      'categorias_hierarquicas.lvl1', 
                      'categorias_hierarquicas.lvl2'
                    ]}
                    limit={10}
                    showMore={true}
                    showMoreLimit={20}
                    translations={{
                      showMore: 'Voir plus',
                      showLess: 'Voir moins'
                    }}
                  />
                </div>
              </div>
            </aside>

            <div className="search-results">
              <CustomHits />
              
              <div className="pagination-section">
                <Pagination
                  padding={3}
                  showFirst={false}
                  showLast={false}
                  translations={{
                    previousPageItemText: 'Précédent',
                    nextPageItemText: 'Suivant'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Filter Drawer */}
        <div className={`mobile-filter-overlay ${mobileFiltersOpen ? 'show' : ''}`}>
          <div className="mobile-filter-drawer">
            <div className="mobile-filter-header">
              <h3 className="mobile-filter-title">Filtres</h3>
              <button 
                className="mobile-filter-close"
                onClick={() => setMobileFiltersOpen(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="mobile-filter-content">
              {/* Store Availability Toggle */}
              <div className="store-filter-section" style={{ marginBottom: 24 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#fff',
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: '1px solid #e8e0e8',
                  boxShadow: '0 2px 8px rgba(75,22,76,0.06)',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#4B164C' }}>
                    Disponible en ma succursale
                  </div>
                  
                  <button
                    onClick={() => setStoreFilter(!storeFilter)}
                    style={{
                      position: 'relative',
                      width: 52,
                      height: 28,
                      background: storeFilter ? '#4B164C' : '#e8e0e8',
                      border: 'none',
                      borderRadius: 14,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      padding: 2
                    }}
                    disabled={!selectedStore}
                  >
                    <div style={{
                      width: 24,
                      height: 24,
                      background: '#fff',
                      borderRadius: '50%',
                      transform: storeFilter ? 'translateX(24px)' : 'translateX(0)',
                      transition: 'transform 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MapPin size={12} color={storeFilter ? "#4B164C" : "#888"} weight="bold" />
                    </div>
                  </button>
                </div>
                
                {!selectedStore && (
                  <div style={{
                    background: '#fff3e0',
                    border: '1px solid #ffcc80',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginTop: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f57c00" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span style={{ fontSize: 14, color: '#f57c00' }}>
                      Sélectionnez une succursale pour filtrer par disponibilité
                    </span>
                  </div>
                )}
              </div>

              {/* Boolean Toggle Filters */}
              <div className="facet-group">
                <h4>Statut du produit</h4>
                <div className="toggle-filters">
                  <ToggleRefinement
                    attribute="inStock"
                    label="En stock"
                  />
                  <ToggleRefinement
                    attribute="availability_front"
                    label="Disponible en magasin"
                  />
                </div>
              </div>

              {/* Current Refinements */}
              <div className="current-refinements">
                <CurrentRefinements
                  translations={{
                    clearButtonText: '✕'
                  }}
                />
              </div>

              {/* Grape Variety */}
              <div className="facet-group">
                <h4>Cépage</h4>
                <RefinementList
                  attribute="attributes.cepage"
                  limit={10}
                  showMore={true}
                  showMoreLimit={30}
                  translations={{
                    showMore: 'Voir plus',
                    showLess: 'Voir moins',
                    noRefinementRoot: 'Aucun cépage disponible'
                  }}
                />
              </div>

              {/* Country of Origin */}
              <div className="facet-group">
                <h4>Pays d'origine</h4>
                <RefinementList
                  attribute="attributes.pays_origine"
                  limit={15}
                  showMore={true}
                  showMoreLimit={30}
                  translations={{
                    showMore: 'Voir plus',
                    showLess: 'Voir moins',
                    noRefinementRoot: 'Aucun pays disponible'
                  }}
                />
              </div>

              {/* Region of Origin */}
              <div className="facet-group">
                <h4>Région d'origine</h4>
                <RefinementList
                  attribute="attributes.region_origine"
                  limit={15}
                  showMore={true}
                  showMoreLimit={30}
                  translations={{
                    showMore: 'Voir plus',
                    showLess: 'Voir moins',
                    noRefinementRoot: 'Aucune région disponible'
                  }}
                />
              </div>

              {/* Sub-region */}
              <div className="facet-group">
                <h4>Sous-région</h4>
                <RefinementList
                  attribute="attributes.sous_region_origine_1"
                  limit={15}
                  showMore={true}
                  showMoreLimit={30}
                  translations={{
                    showMore: 'Voir plus',
                    showLess: 'Voir moins',
                    noRefinementRoot: 'Aucune sous-région disponible'
                  }}
                />
              </div>

              {/* Product Status */}
              <div className="facet-group">
                <h4>Statut du produit</h4>
                <RefinementList
                  attribute="attributes.statut_produit"
                  limit={10}
                  showMore={true}
                  showMoreLimit={20}
                  translations={{
                    showMore: 'Voir plus',
                    showLess: 'Voir moins',
                    noRefinementRoot: 'Aucun statut disponible'
                  }}
                />
              </div>

              {/* Product Identity */}
              <div className="facet-group">
                <h4>Identité du produit</h4>
                <RefinementList
                  attribute="attributes.identite_produit"
                  limit={10}
                  showMore={true}
                  showMoreLimit={20}
                  translations={{
                    showMore: 'Voir plus',
                    showLess: 'Voir moins',
                    noRefinementRoot: 'Aucune identité disponible'
                  }}
                />
              </div>

              {/* Price Range */}
              <div className="facet-group">
                <h4>Fourchette de prix</h4>
                <RangeSlider
                  min={0}
                  max={1000}
                  onRangeChange={handlePriceRangeChange}
                />
              </div>

              {/* Categories */}
              <div className="facet-group">
                <h4>Catégories</h4>
                <HierarchicalMenu
                  attributes={[
                    'categorias_hierarquicas.lvl0',
                    'categorias_hierarquicas.lvl1', 
                    'categorias_hierarquicas.lvl2'
                  ]}
                  limit={10}
                  showMore={true}
                  showMoreLimit={20}
                  translations={{
                    showMore: 'Voir plus',
                    showLess: 'Voir moins'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </InstantSearch>
    </main>
  );
}; 